import React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";

const firstValue = (row, keys, fallback = "") => {
  for (const key of keys) {
    if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") return row[key];
  }
  return fallback;
};

const uniqueTextList = (...values) => Array.from(new Set(
  values
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .map((value) => String(value || "").trim())
    .filter(Boolean)
));

const sectionLabelSx = {
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.45,
};

const richTextSx = {
  color: "text.primary",
  fontSize: "0.9rem",
  lineHeight: 1.7,
  "& h1, & h2, & h3": { mt: 0, mb: 0.75, fontWeight: 800 },
  "& h1": { fontSize: "1.15rem" },
  "& h2": { fontSize: "1.02rem" },
  "& h3": { fontSize: "0.92rem" },
  "& p": { mt: 0, mb: 1 },
  "& ul, & ol": { mt: 0.5, mb: 0, pl: 2.5 },
  "& li": { mb: 0.4 },
  "& strong, & b": { fontWeight: 800 },
};

const renderPlainText = (text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return null;

  const nodes = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    nodes.push(
      <Box component="ul" key={`bullets-${nodes.length}`} sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
        {items.map((item, index) => (
          <Typography component="li" variant="body2" key={`${item}-${index}`} sx={{ mb: 0.4 }}>
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  lines.forEach((line) => {
    const bulletMatch = line.match(/^[-•*]\s+(.+)$/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1]);
      return;
    }
    flushBullets();
    const isHeading = line.length <= 80 && !/[.!?]$/.test(line);
    nodes.push(
      <Typography
        key={`line-${nodes.length}`}
        variant={isHeading ? "subtitle2" : "body2"}
        sx={{ fontWeight: isHeading ? 800 : 400, mb: isHeading ? 0.5 : 1, lineHeight: 1.7 }}
      >
        {line.replace(/:$/, "")}
      </Typography>
    );
  });
  flushBullets();
  return nodes;
};

const ContractorProfileContent = ({
  profile = null,
  title = "Profile",
  emptyMessage = "Profile is not available yet.",
}) => {
  const row = profile && typeof profile === "object" ? profile : {};
  const workProfile = firstValue(row, ["workProfile", "work_profile"], {}) || {};
  const headline = String(firstValue(row, ["headline"], firstValue(workProfile, ["headline"], "")) || "").trim();
  const summary = String(firstValue(row, ["summary"], firstValue(workProfile, ["summary"], "")) || "").trim();
  const skills = uniqueTextList(
    firstValue(row, ["skills"], []),
    firstValue(workProfile, ["skills"], []),
    firstValue(workProfile, ["tools"], [])
  );
  const domainFocus = uniqueTextList(
    firstValue(row, ["domainFocus", "domain_focus"], []),
    firstValue(workProfile, ["domainFocus", "domain_focus", "job_functions"], [])
  );
  const experienceSummary = uniqueTextList(
    firstValue(row, ["experienceSummary", "experience_summary"], []),
    firstValue(workProfile, ["experienceSummary", "experience_summary"], [])
  );
  const generatedHtml = String(firstValue(row, ["generatedResumeHtml", "generated_resume_html"], "") || "").trim();
  const generatedText = String(firstValue(row, ["generatedResume", "generated_resume"], "") || "").trim();
  const hasStructuredProfile = Boolean(headline || summary || skills.length || domainFocus.length || experienceSummary.length);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.25, bgcolor: "rgba(25, 118, 210, 0.04)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>

      {hasStructuredProfile ? (
        <Stack spacing={1.25}>
          {headline && (
            <Typography variant="body1" sx={{ fontWeight: 800 }}>
              {headline}
            </Typography>
          )}

          {summary && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                Overall summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {summary}
              </Typography>
            </Box>
          )}

          {skills.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                Skillset
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 0.75 }}>
                {skills.map((skill) => (
                  <Chip key={skill} size="small" label={skill} sx={{ borderRadius: 1, fontWeight: 700 }} />
                ))}
              </Stack>
            </Box>
          )}

          {domainFocus.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                Job functions
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 0.75 }}>
                {domainFocus.map((item) => (
                  <Chip key={item} size="small" variant="outlined" label={item} sx={{ borderRadius: 1 }} />
                ))}
              </Stack>
            </Box>
          )}

          {experienceSummary.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                Experience highlights
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                {experienceSummary.map((item) => (
                  <Typography component="li" variant="body2" key={item} sx={{ mb: 0.4 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      ) : generatedHtml ? (
        <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generatedHtml) }} />
      ) : generatedText ? (
        <Box sx={richTextSx}>{renderPlainText(generatedText)}</Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}
    </Paper>
  );
};

export default ContractorProfileContent;
